import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { getApiError } from '../../lib/axios';
import { Search, ChevronLeft, ChevronRight, Shield, ShieldOff, Loader2, Crown, UserMinus } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  plan: string;
  auth_provider: string;
  is_active: boolean;
  is_verified: boolean;
  is_admin: boolean;
  created_at: string | null;
  last_login: string | null;
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const currentUser = useAuthStore(s => s.user);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const perPage = 20;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (search) params.set('search', search);
      const response = await api.get(`/api/admin/users?${params}`);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err: any) {
      if (err.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const toggleActive = async (userId: string, currentActive: boolean) => {
    setUpdating(userId); setError(null);
    try {
      await api.patch(`/api/admin/users/${userId}`, { is_active: !currentActive });
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: !currentActive } : u));
    } catch (err) {
      setError(getApiError(err));
    }
    setUpdating(null);
  };

  const toggleAdmin = async (user: AdminUser) => {
    if (currentUser?.id === user.id) {
      setError("You can't change your own admin status. Ask another admin or run a SQL update.");
      return;
    }
    const next = !user.is_admin;
    const label = user.full_name || user.email;
    const verb = next ? 'Grant admin access to' : 'Revoke admin from';
    if (!confirm(`${verb} ${label}?`)) return;

    setUpdating(user.id); setError(null);
    try {
      await api.patch(`/api/admin/users/${user.id}`, { is_admin: next });
      setUsers(users.map(u => u.id === user.id ? { ...u, is_admin: next } : u));
    } catch (err) {
      setError(getApiError(err));
    }
    setUpdating(null);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{total} total users</p>
          </div>
          <form onSubmit={handleSearch} className="mt-4 sm:mt-0 flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg">
              Search
            </button>
          </form>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 text-sm text-rose-700 dark:text-rose-300 flex items-center justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700 text-xs font-semibold">dismiss</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">User</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Plan</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Provider</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Joined</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{u.full_name || u.email.split('@')[0]}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{u.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded text-xs font-medium capitalize bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                            {u.plan}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 capitalize">{u.auth_provider}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              u.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                            {u.is_admin && <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">Admin</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleAdmin(u)}
                              disabled={updating === u.id || currentUser?.id === u.id}
                              className="p-1.5 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title={
                                currentUser?.id === u.id
                                  ? "Can't change your own admin status"
                                  : u.is_admin ? 'Revoke admin' : 'Make admin'
                              }
                            >
                              {u.is_admin
                                ? <UserMinus className="w-4 h-4 text-purple-600" />
                                : <Crown className="w-4 h-4 text-purple-500" />}
                            </button>
                            <button
                              onClick={() => toggleActive(u.id, u.is_active)}
                              disabled={updating === u.id}
                              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                              title={u.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {u.is_active ? <ShieldOff className="w-4 h-4 text-red-500" /> : <Shield className="w-4 h-4 text-green-500" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
